import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { X, Download, Smartphone } from 'lucide-react';

const enconOrange = '#ee7203';

const InstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Check if already installed as PWA
    const standalone = window.matchMedia('(display-mode: standalone)').matches || 
                      window.navigator.standalone === true;
    setIsStandalone(standalone);

    // Detect device
    const userAgent = navigator.userAgent.toLowerCase();
    const isIOSDevice = /iphone|ipad|ipod/.test(userAgent);
    const isAndroidDevice = /android/.test(userAgent);
    
    setIsIOS(isIOSDevice);
    setIsAndroid(isAndroidDevice);

    // Check if user has dismissed the prompt before
    const dismissed = localStorage.getItem('installPromptDismissed');
    const dismissedTime = dismissed ? parseInt(dismissed) : 0;
    const oneWeek = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds
    
    // Don't show if dismissed less than a week ago
    if (dismissed && (Date.now() - dismissedTime) < oneWeek) {
      return;
    }

    // Listen for the beforeinstallprompt event (Android/Chrome)
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Show prompt after a short delay
      setTimeout(() => setShowPrompt(true), 2000);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // For iOS, show manual instructions after delay
    if (isIOSDevice && !standalone) {
      setTimeout(() => setShowPrompt(true), 3000);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      // Trigger the native install prompt
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        setShowPrompt(false);
      }
      setDeferredPrompt(null);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('installPromptDismissed', Date.now().toString());
  };

  // Don't show if already installed or no prompt available
  if (isStandalone || !showPrompt) {
    return null;
  }

  return (
    <div className="fixed bottom-4 left-4 right-4 z-[9999] md:left-auto md:right-4 md:w-96 animate-slideUp">
      <div 
        className="bg-white rounded-2xl shadow-2xl border-2 p-4"
        style={{ borderColor: enconOrange }}
      >
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <div 
              className="w-12 h-12 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: enconOrange }}
            >
              <Smartphone className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="font-bold text-gray-900">Install App</h3>
              <p className="text-sm text-gray-500">Add to your home screen</p>
            </div>
          </div>
          <button 
            onClick={handleDismiss}
            className="p-1 rounded-full hover:bg-gray-100"
          >
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* Android with native prompt */}
        {deferredPrompt && (
          <div className="space-y-3">
            <p className="text-sm text-gray-600">
              Install C30 Dashboard for quick access and offline support.
            </p>
            <Button
              onClick={handleInstall}
              className="w-full py-3 font-semibold"
              style={{ backgroundColor: enconOrange }}
            >
              <Download className="w-5 h-5 mr-2" />
              Add to Home Screen
            </Button>
          </div>
        )}

        {/* iOS instructions */}
        {isIOS && !deferredPrompt && (
          <div className="space-y-3">
            <p className="text-sm text-gray-600">
              To install this app on your iPhone/iPad:
            </p>
            <ol className="text-sm text-gray-600 space-y-2 ml-4">
              <li className="flex items-start gap-2">
                <span className="font-bold" style={{ color: enconOrange }}>1.</span>
                Tap the <strong>Share</strong> button 
                <span className="inline-block mx-1">
                  <svg className="w-5 h-5 inline" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M16 5l-1.42 1.42-1.59-1.59V16h-1.98V4.83L9.42 6.42 8 5l4-4 4 4zm4 5v11c0 1.1-.9 2-2 2H6c-1.11 0-2-.9-2-2V10c0-1.11.89-2 2-2h3v2H6v11h12V10h-3V8h3c1.1 0 2 .89 2 2z"/>
                  </svg>
                </span>
                at the bottom
              </li>
              <li className="flex items-start gap-2">
                <span className="font-bold" style={{ color: enconOrange }}>2.</span>
                Scroll down and tap <strong>"Add to Home Screen"</strong>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-bold" style={{ color: enconOrange }}>3.</span>
                Tap <strong>"Add"</strong> to confirm
              </li>
            </ol>
          </div>
        )}

        {/* Android without native prompt - manual instructions */}
        {isAndroid && !deferredPrompt && (
          <div className="space-y-3">
            <p className="text-sm text-gray-600">
              To install this app on your Android device:
            </p>
            <ol className="text-sm text-gray-600 space-y-2 ml-4">
              <li className="flex items-start gap-2">
                <span className="font-bold" style={{ color: enconOrange }}>1.</span>
                Tap the <strong>menu</strong> (⋮) in the top right corner
              </li>
              <li className="flex items-start gap-2">
                <span className="font-bold" style={{ color: enconOrange }}>2.</span>
                Tap <strong>"Add to Home screen"</strong> or <strong>"Install app"</strong>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-bold" style={{ color: enconOrange }}>3.</span>
                Tap <strong>"Add"</strong> or <strong>"Install"</strong> to confirm
              </li>
            </ol>
          </div>
        )}

        <button
          onClick={handleDismiss}
          className="w-full mt-3 text-sm text-gray-500 hover:text-gray-700"
        >
          Maybe later
        </button>
      </div>
    </div>
  );
};

export default InstallPrompt;
